import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { TokenService } from '../auth/token.service';
import { AuthService } from '../auth/auth.service';
import { AuthStore } from '../auth/auth-store.service';

const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/register',
  '/auth/forgot-password'
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private injector: Injector) {}

  private get tokenService(): TokenService {
    return this.injector.get(TokenService);
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get authStore(): AuthStore {
    return this.injector.get(AuthStore);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.headers.has('X-Skip-Auth')) {
      return next.handle(req.clone({ headers: req.headers.delete('X-Skip-Auth') }));
    }

    // 🚫 Skip auth endpoints
    if (AUTH_ENDPOINTS.some(url => req.url.includes(url))) {
      return next.handle(req);
    }

    const accessToken = this.tokenService.getAccessToken();

    const authReq = accessToken
      ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {

        // 🚫 Already retried OR not auth-related
        if (
          error.status !== 401 ||
          authReq.headers.has('X-Retry') ||
          !accessToken
        ) {
          return throwError(() => error);
        }

        // 🔄 Swallow first 401 & retry after refresh
        return this.handle401Error(
          authReq.clone({
            headers: authReq.headers.set('X-Retry', 'true')
          }),
          next
        );
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler) {

    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token =>
          next.handle(
            req.clone({
              setHeaders: { Authorization: `Bearer ${token}` }
            })
          )
        )
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return this.tokenService.refreshToken().pipe(
      switchMap((res: any) => {
        this.isRefreshing = false;

        const newAccess =
          res?.data?.accessToken ||
          res?.accessToken ||
          res?.token;

        if (!newAccess) {
          this.forceLogout();
          return throwError(() => 'Refresh failed');
        }

        this.refreshSubject.next(String(newAccess));
        this.authStore.updateAccessToken(String(newAccess));

        return next.handle(
          req.clone({
            setHeaders: { Authorization: `Bearer ${newAccess}` }
          })
        );
      }),
      catchError(err => {
        this.isRefreshing = false;
        const waitingRequests = this.refreshSubject;
        this.refreshSubject = new BehaviorSubject<string | null>(null);
        waitingRequests.error(err);
        this.forceLogout();
        return throwError(() => err);
      })
    );
  }

  private forceLogout() {
    this.isRefreshing = false;
    this.refreshSubject.next(null);
    this.authService.logout('auth-refresh-failed');
  }
}
